import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { EventBusContextType, EventSubscriber, EventSubscription, EventType } from './EventBusContext.type';
import { Logger } from '../../utilities';

export const EventBusContext = createContext<EventBusContextType>({
  subscribe: () => () => {},
  publish: () => {},
  unsubscribe: () => {},
  getSubscribers: () => [],
});

export const EventBusProvider = ({ children }: { children: React.ReactNode }) => {
  const subscriptionsRef = useRef<Map<EventType, Map<string, EventSubscriber>>>(new Map());
  const subscriptionCounterRef = useRef(0);

  const subscribe = useCallback((eventType: EventType, callback: EventSubscriber) => {
    const subscriptionId = `sub_${eventType}_${++subscriptionCounterRef.current}_${Date.now()}`;

    if (!subscriptionsRef.current.has(eventType)) {
      subscriptionsRef.current.set(eventType, new Map());
    }

    const eventSubscriptions = subscriptionsRef.current.get(eventType);
    eventSubscriptions?.set(subscriptionId, callback as EventSubscriber);

    Logger.debug(`EventBus: Subscribed to ${eventType} with id ${subscriptionId}`);

    // Return unsubscribe function
    return () => {
      const eventSubs = subscriptionsRef.current.get(eventType);
      if (eventSubs) {
        eventSubs.delete(subscriptionId);
        Logger.debug(`EventBus: Unsubscribed from ${eventType} with id ${subscriptionId}`);

        // Clean up empty maps
        if (eventSubs.size === 0) {
          subscriptionsRef.current.delete(eventType);
        }
      }
    };
  }, []);

  const publish = useCallback((eventType: EventType, payload: any) => {
    const eventSubscriptions = subscriptionsRef.current.get(eventType);

    if (!eventSubscriptions || eventSubscriptions.size === 0) {
      Logger.debug(`EventBus: No subscribers for event ${eventType}`);
      return;
    }

    Logger.debug(`EventBus: Publishing event ${eventType} to ${eventSubscriptions.size} subscriber(s)`);

    // Execute all callbacks for this event type
    eventSubscriptions.forEach((callback, subscriptionId) => {
      try {
        callback(payload);
      } catch (error) {
        Logger.error(`EventBus: Error in subscriber ${subscriptionId} for event ${eventType}`, error);
      }
    });
  }, []);

  const unsubscribe = useCallback((subscriptionId: string) => {
    // Extract event type from subscription ID (format: sub_{eventType}_{counter}_{timestamp})
    const parts = subscriptionId.split('_');
    if (parts.length < 3) {
      Logger.warn(`EventBus: Invalid subscription ID format: ${subscriptionId}`);
      return;
    }

    // Reconstruct event type (handles event types with underscores)
    const eventType = parts.slice(1, -2).join('_') as EventType;
    const eventSubscriptions = subscriptionsRef.current.get(eventType);

    if (eventSubscriptions) {
      eventSubscriptions.delete(subscriptionId);
      Logger.debug(`EventBus: Unsubscribed ${subscriptionId} from ${eventType}`);

      // Clean up empty maps
      if (eventSubscriptions.size === 0) {
        subscriptionsRef.current.delete(eventType);
      }
    }
  }, []);

  const getSubscribers = useCallback((eventType: EventType): EventSubscription[] => {
    const eventSubscriptions = subscriptionsRef.current.get(eventType);
    if (!eventSubscriptions) {
      return [];
    }

    return Array.from(eventSubscriptions.entries()).map(([id, callback]) => ({
      id,
      eventType,
      callback,
    }));
  }, []);

  const value = useMemo(
    () => ({
      subscribe,
      publish,
      unsubscribe,
      getSubscribers,
    }),
    [subscribe, publish, unsubscribe, getSubscribers]
  );

  return <EventBusContext.Provider value={value}>{children}</EventBusContext.Provider>;
};

export const useEventBus = () => {
  const context = useContext(EventBusContext);
  if (!context) {
    throw new Error('useEventBus must be used within an EventBusProvider');
  }
  return context;
};

